import * as Sentry from "@sentry/react"
import { Component, type ReactNode } from "react"

interface Props {
	children: ReactNode
}

interface State {
	error: Error | null
}

function isChunkError(error: Error): boolean {
	const msg = error.message
	return (
		msg.includes("Failed to fetch dynamically imported module") ||
		msg.includes("Loading chunk") ||
		msg.includes("Loading CSS chunk") ||
		msg.includes("Importing a module script failed")
	)
}

export default class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = { error: null }
	}

	static getDerivedStateFromError(error: Error) {
		return { error }
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		// Auto-reload for chunk errors (stale deploy) instead of showing error UI
		if (isChunkError(error)) {
			const last = Number(sessionStorage.getItem("chunk_reload") || 0)
			if (Date.now() - last > 10_000) {
				sessionStorage.setItem("chunk_reload", String(Date.now()))
				window.location.reload()
				return
			}
		}
		Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
	}

	render() {
		if (this.state.error) {
			return (
				<div className="flex min-h-screen flex-col items-center justify-center bg-black px-6 text-center text-white">
					<p className="mb-3 font-semibold text-lg text-red-400">Something went wrong</p>
					<pre className="max-w-xl overflow-auto whitespace-pre-wrap rounded-lg bg-white/5 p-4 font-mono text-sm text-white/50">
						{this.state.error.message}
					</pre>
					<button
						className="mt-6 rounded-lg bg-white/10 px-4 py-2 text-sm text-white transition-colors hover:bg-white/15"
						onClick={() => window.location.reload()}
					>
						Reload
					</button>
				</div>
			)
		}

		return this.props.children
	}
}
